import axios, { type AxiosInstance, type AxiosError } from 'axios';
import type { ApiError } from '@/types/errors';
import { BACKEND_API_URL } from '@/config/endpoints';

class ApiService {
  private client: AxiosInstance;

  constructor(baseURL: string = BACKEND_API_URL) {
    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.data != null && typeof error.response.data === 'object') {
          const errorData = error.response.data as { error?: ApiError };
          if (errorData.error !== undefined) {
            const err = new Error(errorData.error.message);
            Object.assign(err, errorData.error);
            throw err;
          }
        }
        
        // Fallback error
        const err = new Error(error.message);
        Object.assign(err, {
          code: 'NETWORK_ERROR',
          retryable: true,
        });
        throw err;
      }
    );
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }
}

export const api = new ApiService();
